'use client'

import Link from 'next/link'
import { useCallback, useEffect, useRef } from 'react'
import { Modal } from '@/Modal/Modal'
import { setAdvertisingModalOpenState } from './advertisingModalState'
import type { AdvertisingModalImageCallbacks } from './types'

export interface AdvertisingImageModalProps {
  isOpen: boolean
  onClose: () => void
  imageUrl: string
  imageAlt: string
  href: string
  label?: string
  callbacks?: AdvertisingModalImageCallbacks
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl'
  showCloseButton?: boolean
  closeOnBackdropClick?: boolean
  className?: string
}

export const AdvertisingImageModal = ({
  isOpen,
  onClose,
  imageUrl,
  imageAlt,
  href,
  label,
  callbacks = {},
  maxWidth = '3xl',
  showCloseButton = true,
  closeOnBackdropClick = true,
  className = '',
}: AdvertisingImageModalProps) => {
  const callbacksRef = useRef(callbacks)
  const hasShownEventRef = useRef(false)
  const hasClickedEventRef = useRef(false)
  const hasClosedEventRef = useRef(false)

  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  useEffect(() => {
    if (!isOpen) {
      hasShownEventRef.current = false
      hasClickedEventRef.current = false
      hasClosedEventRef.current = false
      setAdvertisingModalOpenState(false)
      return
    }

    setAdvertisingModalOpenState(true)
    if (!hasShownEventRef.current) {
      callbacksRef.current.onModalShown?.()
      hasShownEventRef.current = true
    }

    return () => {
      setAdvertisingModalOpenState(false)
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    if (!hasClosedEventRef.current) {
      callbacksRef.current.onModalClose?.()
      hasClosedEventRef.current = true
    }
    onClose()
  }, [onClose])

  const handleImageClick = useCallback(() => {
    if (!hasClickedEventRef.current) {
      callbacksRef.current.onImageClick?.()
      hasClickedEventRef.current = true
    }
  }, [])

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={undefined}
      showCloseButton={showCloseButton}
      closeOnBackdropClick={closeOnBackdropClick}
      maxWidth={maxWidth}
      className={`rounded-lg ${className}`.trim()}
      backdropOpacity="soft"
    >
      <Link
        href={href}
        prefetch={false}
        onClick={handleImageClick}
        aria-label={label ?? imageAlt}
        className="block animate-ad-popup-shake"
      >
        <img
          src={imageUrl}
          alt={imageAlt}
          className="block h-auto w-full rounded-lg"
        />
      </Link>
    </Modal>
  )
}
